import type { NextAction, SupportContact } from "../api/types.js";
import type { ReceiptModel } from "../app/receipt-model.js";
import type { Delta, SuccessModel, Way } from "../app/success-model.js";
import type { ScheduledSuccess } from "../app/schedule-success.js";
import { el } from "../lib/dom.js";
import { arrowTo, keepParts, keepTogether } from "../lib/keep.js";
import { icon, type IconId } from "../lib/icon.js";
import type { Inert } from "../app/describe.js";
import { inertNotice, refs } from "./inert.js";
import { bindDrag, exitMs } from "./sheet-drag.js";

/**
 * The bank's sheet (UX spec v3 §6.3, §6.8, §C.8): one component for every sheet.
 * It lives in a modal `<dialog>` (`showModal()`: the page behind is inert and the
 * dialog is labelled by the sheet's title), with the design's `.backdrop` and
 * `.sheet` inside it, so the design's own styles and motion apply unchanged.
 *
 * Every sheet dismisses the same way (§C.8): drag it down, tap above it (the
 * backdrop), tap the grabber, or Esc — and each means the sheet's own "not now",
 * which the caller passes as `onDismiss` (close, keep it, nothing is signed). While
 * a ceremony is in flight the caller locks it: a drag resists 16 px and returns,
 * a tap above nudges it, Esc does nothing.
 *
 * The markup is always the final state — the rise, the check and the count-up are
 * decoration over it, and under reduced motion nothing lasts longer than 1 ms.
 */
let sheetSeq = 0;

export interface SheetHandle {
  readonly element: HTMLElement;
  readonly dialog: HTMLDialogElement;
  close(): Promise<void>;
  /** A ceremony or a request is in flight: nothing dismisses the sheet until it resolves. */
  lock(on: boolean): void;
}

export interface OpenOptions {
  /** The regions behind the sheet (body, topbar, dock). */
  readonly behind: readonly HTMLElement[];
  readonly reduced: boolean;
  readonly animate?: boolean;
  /** Drag down, tap above, the grabber, Esc: what "dismiss" means for this sheet (§C.8's table). */
  readonly onDismiss: () => void;
}

/** Back closes one layer (DCTA-84 A1): an open sheet is one history entry (`lib/nav.ts`). */
export interface SheetHistory {
  push(pop: () => boolean): number;
  done(id: number): void;
}
let history: SheetHistory | undefined;
export function useSheetHistory(h: SheetHistory | undefined): void {
  history = h;
}

export function openSheet(host: HTMLElement, sheet: HTMLElement, opts: OpenOptions): SheetHandle {
  const backdrop = el("div", { class: "backdrop", aria: { hidden: "true" } });
  const title = sheet.querySelector<HTMLElement>("h2");
  const dialog = el("dialog", { class: "sheet-host", ...(title?.id ? { aria: { labelledby: title.id } } : {}) }, [backdrop, sheet]);
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  for (const region of opts.behind) {
    region.setAttribute("aria-hidden", "true");
    region.inert = true;
  }
  host.append(dialog);
  if (typeof dialog.showModal === "function") dialog.showModal();
  else dialog.setAttribute("open", "");

  let locked = false;
  let closed = false;
  sheet.dataset["locked"] = "false";
  const nudge = (): void => {
    if (opts.reduced || typeof sheet.animate !== "function") return;
    sheet.animate([{ transform: "translateY(0)" }, { transform: "translateY(10px)" }, { transform: "translateY(0)" }], {
      duration: 280,
      easing: "cubic-bezier(.2,.8,.2,1)",
    });
  };
  const dismiss = (): void => {
    if (closed) return;
    if (locked) {
      nudge();
      return;
    }
    opts.onDismiss();
  };
  // The system's Back is one more way to dismiss (§C.8's meaning per sheet); a locked sheet
  // refuses and keeps its entry — a ceremony in flight is never interrupted by Back.
  const entry = history?.push(() => {
    if (closed) return true;
    if (locked) {
      nudge();
      return false;
    }
    opts.onDismiss();
    return true;
  });
  backdrop.dataset["dismiss"] = "bound";
  backdrop.addEventListener("click", dismiss);
  sheet.querySelector<HTMLElement>(".handle")?.addEventListener("click", dismiss);
  // Esc arrives as the dialog's `cancel`: the dialog never closes itself — the sheet's own
  // dismiss decides (and a locked sheet stays).
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    dismiss();
  });
  let leaveMs = 240;
  bindDrag(sheet, backdrop, {
    locked: () => locked,
    reduced: () => opts.reduced,
    dismiss: (fromY) => {
      leaveMs = exitMs(fromY, sheet.getBoundingClientRect().height);
      dismiss();
    },
  });

  // One frame at the start position, then the rise (a class, never a hidden default).
  void sheet.offsetWidth;
  backdrop.classList.add("in");
  sheet.classList.add("in");
  if (opts.animate === true && !opts.reduced) sheet.classList.add("animate");
  if (title !== null) {
    title.tabIndex = -1;
    title.focus({ preventScroll: true });
  }
  return {
    element: sheet,
    dialog,
    lock: (on: boolean) => {
      locked = on;
      sheet.dataset["locked"] = on ? "true" : "false";
    },
    close: () => {
      if (closed) return Promise.resolve();
      closed = true;
      if (entry !== undefined) history?.done(entry);
      // It leaves from wherever it is (a drag left it part-way down), faster the further it went.
      const ms = opts.reduced ? 1 : leaveMs;
      sheet.style.transitionDuration = `${String(ms)}ms`;
      backdrop.style.transitionDuration = `${String(ms)}ms`;
      sheet.classList.add("out");
      backdrop.classList.add("out");
      sheet.classList.remove("in", "dragging");
      backdrop.classList.remove("in", "dragging");
      sheet.style.transform = "";
      backdrop.style.opacity = "";
      return new Promise((resolve) => {
        const done = (): void => {
          if (dialog.open && typeof dialog.close === "function") dialog.close();
          dialog.remove();
          for (const region of opts.behind) {
            region.removeAttribute("aria-hidden");
            region.inert = false;
          }
          previous?.focus({ preventScroll: true });
          resolve();
        };
        if (opts.reduced) done();
        else setTimeout(done, ms + 20);
      });
    },
  };
}

/** `title`: words, or (DCTA-143) words with a flagged string's inert form set apart as data (`refs`). DCTA-162: shared with the Read aloud sheet. */
export function frame(title: string | readonly (Node | string)[], top: HTMLElement, rest: readonly (HTMLElement | null)[]): HTMLElement {
  sheetSeq += 1;
  const id = `sheet-title-${String(sheetSeq)}`;
  // No role here: openSheet's <dialog> is the dialog, labelled by this h2 (DCTA-82).
  return el("div", { class: "sheet" }, [
    el("div", { class: "handle", aria: { hidden: "true" } }),
    top,
    el("h2", { id }, typeof title === "string" ? [title] : title),
    ...rest.filter((x): x is HTMLElement => x !== null),
    el("div", { class: "sheet-end", aria: { hidden: "true" } }),
  ]);
}

/**
 * The circle at the top of a success sheet or a receipt: the check, the clock (held), the × (not sent), or — DCTA-209 (30c)
 * — the minus of a payment a schedule skipped (nothing moved).
 */
function sheetTop(kind: "check" | "hold" | "no" | "minus"): HTMLElement {
  if (kind === "check") return el("div", { class: "check", aria: { hidden: "true" } }, [icon("i-check")]);
  if (kind === "minus") return el("div", { class: "key no", aria: { hidden: "true" } }, [icon("i-minus")]);
  return el("div", { class: `key ${kind}`, aria: { hidden: "true" } }, [icon(kind === "hold" ? "i-clock" : "i-x")]);
}

// ---------------------------------------------------------------- success
/**
 * `onWay` (DCTA-198, P1-B1): a row's way on — "Update my details" under a step the gateway's identity check stopped when it
 * ran — handed back with the bank's action and label, as the refusal's chip is; absent, no way on is drawn.
 */
export function successSheet(
  m: SuccessModel,
  onDone: () => void,
  onCancelHeld?: () => void,
  onReceipt?: () => void,
  onWay?: (action: NextAction, label: string) => void,
): HTMLElement {
  const done = el("button", { class: "raw btn", type: "button" }, ["Done"]);
  done.addEventListener("click", onDone);
  const acts: HTMLElement[] = [done];
  if (m.held !== undefined && onCancelHeld !== undefined) {
    // 06: what the signature reserved and has not sent can still be stopped.
    const cancel = el("button", { class: "raw btn link", type: "button" }, [`Cancel the held ${m.held.amount}`]);
    cancel.addEventListener("click", onCancelHeld);
    acts.push(cancel);
  }
  return frame(
    m.title,
    // DCTA-143 (§F.2 18f): "no" — nothing went through and nothing waits — is the receipt's × circle.
    sheetTop(m.icon),
    [
      m.amount === undefined ? null : el("div", { class: "amount money" }, [m.amount]),
      el("div", { class: "sum" }, keepTogether(m.sum)),
      pill("i-receipt", "Receipt", onReceipt),
      // §F.2 18f: "Nothing left your accounts." in place of What changed; the rows of what did not go follow it.
      m.none === undefined ? el("h3", {}, ["What changed"]) : el("div", { class: "none" }, [m.none]),
      m.deltas.length === 0 ? null : el("div", { class: "dl" }, m.deltas.map((d) => deltaRow(d, m.inert, onWay))),
      el("div", { class: "acts" }, acts),
    ],
  );
}

/**
 * DCTA-209 F56 (09-scheduled.md row 27n, mock-up `set-up`): a signed schedule's success sheet — "Scheduled", the amount,
 * who and from where in the rule's words, First payment and Ends (stacked in large type), "Nothing has left your
 * accounts.", "See it in Activity" (Activity at the top) and Done. The title takes focus as the sheet opens (openSheet).
 */
export function scheduledSheet(m: ScheduledSuccess, h: { readonly onDone: () => void; readonly onSeeIt?: () => void }): HTMLElement {
  const done = el("button", { class: "raw btn done", type: "button" }, ["Done"]);
  done.addEventListener("click", h.onDone);
  return frame(m.title, sheetTop("check"), [
    m.amount === "" ? null : el("div", { class: "amount money" }, [m.amount]),
    el("p", { class: "sum" }, keepTogether(m.sum)),
    m.kv.length === 0 ? null : el("div", { class: "list kv stack" }, m.kv.map((r) => el("div", {}, [el("span", { class: "k" }, [r.k]), el("span", { class: "v" }, [r.v])]))),
    el("p", { class: "none" }, [m.none]),
    pill("i-list", "See it in Activity", h.onSeeIt),
    el("div", { class: "acts" }, [done]),
  ]);
}

/** §6.3's ghost pill under the sum line: this bundle's Receipt (DCTA-75 P2-1), or a schedule's "See it in Activity" (27n). */
function pill(ic: IconId, label: string, onClick: (() => void) | undefined): HTMLElement | null {
  if (onClick === undefined) return null;
  const b = el("button", { class: "raw", type: "button" }, [icon(ic), label]);
  b.addEventListener("click", onClick);
  return el("div", { class: "receipt" }, [b]);
}

// ---------------------------------------------------------------- receipt
export interface ReceiptHandlers {
  onDone(): void;
  /** Verified → how it was checked (the customer sheet) or the proof (judge mode). */
  onVerified?(): void;
  /** 06: the held part can still be stopped from its receipt. */
  onCancelHeld?(): void;
  /** DCTA-209 (30b, 30c): the schedule row — the schedule this payment came from. */
  onSchedule?(mandateId: string): void;
}

/**
 * A receipt (§6.13): the success sheet's parts, read back later. Everything is
 * text from the model; a signed step that did not go carries the bank's own sentence
 * in the notice. DCTA-166: only what the customer signed has a receipt (a refused
 * draft's "Recorded" receipt went with its Activity row; the audit trail keeps it).
 */
export function receiptSheet(m: ReceiptModel, h: ReceiptHandlers): HTMLElement {
  const done = el("button", { class: "raw btn", type: "button" }, ["Done"]);
  done.addEventListener("click", () => h.onDone());
  const acts: HTMLElement[] = [done];
  if (m.cancelHeld !== undefined && h.onCancelHeld !== undefined) {
    const cancel = el("button", { class: "raw btn link", type: "button" }, [`Cancel the held ${m.cancelHeld.amount}`]);
    cancel.addEventListener("click", () => h.onCancelHeld?.());
    acts.push(cancel);
  }
  const top = sheetTop(m.icon);
  // DCTA-143: a flagged string this receipt prints is the bank's inert form, set apart as data wherever it stands, and
  // the bank's sentence about it carries "Show the full text", as on the card (ui/inert.ts).
  const inert = m.inert;
  return frame(refs(m.title, inert), top, [
    m.amount === undefined ? null : el("div", { class: "amount money" }, [m.amount]),
    el("div", { class: "sum" }, keepTogether(m.sum)),
    ...(m.inertNotices ?? []).map((n) => inertNotice(n.sentence, n.fulls)),
    m.refusal === undefined
      ? null
      : el("div", { class: "notice" }, [icon("i-alert"), el("div", { class: "ntxt" }, [m.refusal.text])]),
    m.holdNotice === undefined ? null : el("div", { class: "notice hold" }, [icon("i-clock"), el("div", { class: "ntxt" }, [m.holdNotice])]),
    el(
      "div",
      { class: "kv" },
      m.kv.map((r) =>
        el("div", {}, [
          el("span", { class: "k" }, [r.k]),
          el("span", { class: "v" }, [...refs(r.v, inert), ...(r.small === undefined ? [] : [el("small", {}, refs(r.small, inert))])]),
        ]),
      ),
    ),
    m.deltas.length === 0 ? null : el("h3", {}, ["What changed"]),
    m.deltas.length === 0 ? null : el("div", { class: "dl" }, m.deltas.map((d) => deltaRow(d, inert))),
    m.none === undefined ? null : el("div", { class: "none" }, [m.none]),
    scheduleLine(m, h),
    verifiedLine(m, h),
    el("div", { class: "acts" }, acts),
  ]);
}

/** DCTA-209 (30b, 30c): the schedule a payment came from, above Verified — its rule and when it was signed; it opens the schedule. */
function scheduleLine(m: ReceiptModel, h: ReceiptHandlers): HTMLElement | null {
  const s = m.schedule;
  if (s === undefined) return null;
  const tx = el("span", { class: "tx" }, [s.rule, el("small", {}, [s.sub])]);
  if (h.onSchedule === undefined) return el("div", { class: "verified rec" }, [icon("i-repeat"), tx]);
  const b = el("button", { class: "raw verified rec", type: "button", aria: { label: `${s.rule}. ${s.sub}` } }, [icon("i-repeat"), tx, icon("i-chev-r", "chev")]);
  b.addEventListener("click", () => h.onSchedule?.(s.mandateId));
  return b;
}

function verifiedLine(m: ReceiptModel, h: ReceiptHandlers): HTMLElement | null {
  const v = m.verified;
  if (v === undefined) return null;
  const tx = el("span", { class: "tx" }, [el("b", { class: "vt" }, [v.title]), el("small", { class: "vs" }, [v.sub])]);
  if (h.onVerified === undefined) return el("div", { class: "verified" }, [icon("i-shield"), tx]);
  const b = el("button", { class: "raw verified", type: "button", aria: { label: `${v.title}. ${v.sub}` } }, [
    icon("i-shield"),
    tx,
    icon("i-chev-r", "chev"),
  ]);
  b.addEventListener("click", () => h.onVerified?.());
  return b;
}

/**
 * Behind Verified with judge mode off (§10.5, decision 46): four plain lines. The
 * fourth is said only when the bank's own check of the trail holds; otherwise it
 * says the record can't be checked — never "nothing has changed" over a broken trail.
 */
export function checkedSheet(holds: boolean, onDone: () => void): HTMLElement {
  const done = el("button", { class: "raw btn", type: "button" }, ["Done"]);
  done.addEventListener("click", onDone);
  const line = (text: string, ok: boolean): HTMLElement =>
    el("li", { class: ok ? "" : "bad" }, [icon(ok ? "i-check" : "i-alert"), text]);
  const sheet = frame("How it was checked", el("div", { class: "key", aria: { hidden: "true" } }, [icon("i-shield")]), [
    el("ul", { class: "checked" }, [
      line("You said it", true),
      line("The bank showed it exactly", true),
      line("You signed that", true),
      holds ? line("Nothing has changed since", true) : line("The bank's record of this can't be checked right now. Ask us before you rely on it.", false),
    ]),
    el("div", { class: "acts" }, [done]),
  ]);
  sheet.classList.add("short");
  return sheet;
}

function deltaRow(d: Delta, inert?: readonly Inert[], onWay?: (action: NextAction, label: string) => void): HTMLElement {
  const ba: (Node | string)[] = [];
  if (d.before !== undefined && d.after !== undefined) {
    // P1-6 (DCTA-192): "→ after" is one piece — the line may break before the arrow, never between it and the figure.
    ba.push(d.pre ?? "", d.before, " ", arrowTo(d.after));
  } else if (d.text !== undefined) {
    ba.push(...refs(d.text, inert));
  }
  if (d.when !== undefined) ba.push(el("span", { class: "when" }, keepTogether(d.when)));
  if (d.note !== undefined) ba.push(el("span", { class: "when" }, keepParts(refs(d.note, inert))));
  const row = el("div", { class: "delta" }, [
    el("span", { class: "dir" }, [d.dir]),
    el("span", {}, [
      el("span", { class: "nm" }, refs(d.name, inert)),
      ...(ba.length > 0 ? [el("span", { class: "ba money" }, ba)] : []),
      // DCTA-143 × §F.2 18e (ask F18): a step that did not go — its amount, then the bank's sentence, each its own sub-line
      // in the row's own label type (`.ba`); the sentence is words, not money.
      ...(d.sub === undefined ? [] : [el("span", { class: "ba money" }, [d.sub])]),
      ...(d.said === undefined ? [] : [el("span", { class: "ba" }, [d.said])]),
      // DCTA-198 (P1-B1): the step's way on, under its sentence — the refusal's chip (`.opt`), the bank's label as text.
      ...(d.ways === undefined || onWay === undefined ? [] : d.ways.map((w) => wayChip(w, onWay))),
    ]),
    el("span", { class: d.pos ? "chip pos money" : "chip money" }, [d.chip]),
  ]);
  if (d.count !== undefined) {
    row.dataset["from"] = String(d.count.from);
    row.dataset["to"] = String(d.count.to);
    row.dataset["prefix"] = d.count.prefix;
  }
  return row;
}

/** One tap hands the way on over (the sheet goes with it); a second tap meanwhile does nothing. */
function wayChip(w: Way, onWay: (action: NextAction, label: string) => void): HTMLElement {
  const chip = el("button", { class: "raw opt", type: "button" }, [w.label]);
  let used = false;
  chip.addEventListener("click", () => {
    if (used) return;
    used = true;
    chip.setAttribute("aria-disabled", "true");
    onWay(w.action, w.label);
  });
  return chip;
}

const easeOutQuint = (t: number): number => 1 - (1 - t) ** 5;
const shown = (n: number, prefix: string): string =>
  prefix + Math.abs(n).toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The choreography of §6.3: each row's after figure counts before → after over
 * 600 ms (ease-out-quint) as the row lands — first at 380 ms, then every 80 ms.
 * Only the in-between frames are computed; the last frame puts back the bank's
 * own string, which was in the markup all along.
 */
export function countUp(sheet: HTMLElement, reduced: boolean): void {
  if (reduced) return;
  [...sheet.querySelectorAll<HTMLElement>(".delta")].forEach((row, i) => {
    const after = row.querySelector<HTMLElement>(".after");
    const from = Number(row.dataset["from"]);
    const to = Number(row.dataset["to"]);
    const prefix = row.dataset["prefix"] ?? "";
    if (after === null || row.dataset["from"] === undefined) return;
    const final = after.textContent ?? "";
    after.textContent = shown(from, prefix);
    setTimeout(() => {
      const t0 = performance.now();
      const step = (now: number): void => {
        const p = Math.min(1, (now - t0) / 600);
        after.textContent = p < 1 ? shown(from + (to - from) * easeOutQuint(p), prefix) : final;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, 380 + i * 80 + 120);
  });
}

// ---------------------------------------------------------------- support
/**
 * "Talk to us" (07's safe exit, KYC's "Update my details", 10's not-available): the
 * bank's number and hours as `GET /v1/support` sends them — the operator's config,
 * one source, because a support number is exactly what a scam would substitute.
 * The call is a `tel:` link only when the number is digits; anything else is text.
 */
const TEL = /^\+?[0-9]{6,20}$/;

export function supportSheet(c: SupportContact, onClose: () => void): HTMLElement {
  const acts: HTMLElement[] = [];
  if (TEL.test(c.hotline_tel)) acts.push(el("a", { class: "raw btn", href: `tel:${c.hotline_tel}` }, [`Call ${c.hotline}`]));
  const close = el("button", { class: acts.length === 0 ? "raw btn" : "raw btn ghost", type: "button" }, ["Close"]);
  close.addEventListener("click", onClose);
  acts.push(close);
  const sheet = frame("Talk to us", el("div", { class: "key", aria: { hidden: "true" } }, [icon("i-phone")]), [
    el("p", { class: "lead" }, [c.hours]),
    ...(acts.length === 1 ? [el("p", { class: "fine" }, [c.hotline])] : []),
    el("div", { class: "acts" }, acts),
  ]);
  sheet.classList.add("short");
  return sheet;
}

// ---------------------------------------------------------------- passkey
/**
 * §C.11: two passkey sheets are left. "Your passkey isn't on this phone" is recovery
 * only — the bank has a passkey, this phone offered none — and its dismiss means "not
 * now" (nothing is signed); while its create is in flight the caller locks it and its
 * primary stays busy. "Not supported" is informational. "I already have a passkey",
 * "Create a new one instead" and "Passkey ready" are gone everywhere.
 */
export interface PasskeySheetActions {
  readonly primary: { readonly label: string; readonly onSelect: () => void; readonly busy?: boolean };
  readonly secondary?: { readonly label: string; readonly onSelect: () => void };
  readonly link?: { readonly label: string; readonly onSelect: () => void };
}

export type PasskeyKind = "no-match" | "unsupported";

const PASSKEY: Readonly<Record<PasskeyKind, { title: string; lead: string; fine: string }>> = {
  "no-match": {
    title: "Your passkey isn't on this phone",
    lead: "The bank has one for you on another device. Use that device, or create one on this phone.",
    fine: "Nothing was signed.",
  },
  unsupported: {
    title: "Passkeys aren't available on this phone",
    lead: "It can't hold one, so nothing was saved. Balances still work here; signing needs a phone that can.",
    fine: "",
  },
};

export function passkeySheet(kind: PasskeyKind, actions: PasskeySheetActions): { sheet: HTMLElement; primary: HTMLButtonElement } {
  const c = PASSKEY[kind];
  const primary = el("button", { class: "raw btn", type: "button", aria: { busy: actions.primary.busy === true ? "true" : "false" } }, [
    actions.primary.label,
  ]);
  primary.addEventListener("click", () => {
    if (primary.getAttribute("aria-busy") === "true") return;
    actions.primary.onSelect();
  });
  const acts: HTMLElement[] = [primary];
  for (const [a, cls] of [
    [actions.secondary, "raw btn ghost"],
    [actions.link, "raw btn link"],
  ] as const) {
    if (a === undefined) continue;
    const b = el("button", { class: cls, type: "button" }, [a.label]);
    b.addEventListener("click", a.onSelect);
    acts.push(b);
  }
  const sheet = frame(c.title, el("div", { class: "key", aria: { hidden: "true" } }, [icon("i-passkey")]), [
    el("p", { class: "lead" }, [c.lead]),
    el("div", { class: "acts" }, acts),
    c.fine === "" ? null : el("div", { class: "fine" }, [c.fine]),
  ]);
  sheet.classList.add("short");
  return { sheet, primary };
}

// ------------------------------------------------------------ protective
/**
 * DCTA-111 (round E batch 1, §E.2 asymmetric friction): a tightening — lock my money, freeze a
 * card — is ONE sheet, no passkey: the question, what stops, what it takes to undo, the act and
 * "Not now". The act holds while the bank is asked (`aria-busy`, label kept); a tap on a busy act
 * asks nothing. The caller locks the sheet meanwhile (`SheetHandle.lock`).
 */
export interface ConfirmSpec {
  readonly icon: IconId;
  readonly title: string;
  readonly lead: string;
  readonly fine?: string;
  readonly act: string;
  readonly keep: string;
}

export function confirmSheet(spec: ConfirmSpec, h: { readonly onAct: () => void; readonly onKeep: () => void }): { sheet: HTMLElement; primary: HTMLButtonElement } {
  const primary = el("button", { class: "raw btn", type: "button", aria: { busy: "false" } }, [spec.act]);
  primary.addEventListener("click", () => {
    if (primary.getAttribute("aria-busy") === "true") return;
    h.onAct();
  });
  const keep = el("button", { class: "raw btn link", type: "button" }, [spec.keep]);
  keep.addEventListener("click", h.onKeep);
  const sheet = frame(spec.title, el("div", { class: "key", aria: { hidden: "true" } }, [icon(spec.icon)]), [
    el("p", { class: "lead" }, [spec.lead]),
    spec.fine === undefined ? null : el("p", { class: "fine" }, [spec.fine]),
    el("div", { class: "acts" }, [primary, keep]),
  ]);
  sheet.classList.add("short");
  return { sheet, primary };
}
